import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import NoData from "components/common/NoData";
import Badge from "../../components/common/Badge";
import {
    fetchInstructorLessonRequests,
    getLessonRequestErrorMessage,
} from "api/lessonRequest";
import {
    LESSON_REQUEST_STATUS,
    LESSON_REQUEST_STATUS_LABEL,
    LESSON_REQUEST_STATUS_TONE,
    getRequestTypeMeta,
    isInstructorUser,
    normalizeLessonRequestList,
} from "constants/lessonRequest";
import "./LessonRequest.scss";

const STATUS_TABS = [
    LESSON_REQUEST_STATUS.ALL,
    LESSON_REQUEST_STATUS.PENDING,
    LESSON_REQUEST_STATUS.CONFIRMED,
    LESSON_REQUEST_STATUS.REJECTED,
];

const formatTime = (value) => (value ? String(value).slice(0, 5) : "");

const getCardTitle = (request, meta) => {
    if (request?.request_type === "SPECIAL" && request?.topic) return request.topic;
    return request.request_type_display || meta.title;
};

const getCardSubtitle = (request, meta) => {
    if (request?.request_type === "SPECIAL" && request?.topic) {
        return request.request_type_display || meta.title;
    }
    return request.lesson_product_name || request.sport_name || "수업 요청";
};

const getTopicLabel = (request) => {
    if (request?.request_type === "PREMIUM") return "요청 메모";
    if (request?.request_type === "SPECIAL") return "스페셜 제목";
    return "주제";
};

// 수강생 표시는 name+nickname → nickname only → name only → fallback("요청자") 순으로 방어한다.
// undefined나 빈 문자열, "(nickname)"처럼 앞 괄호만 남는 표시가 노출되지 않도록 한다.
const getRequesterLabel = (requester) => {
    const name = typeof requester?.name === "string" ? requester.name.trim() : "";
    const nickname = typeof requester?.nickname === "string" ? requester.nickname.trim() : "";
    if (name && nickname) return `${name}(${nickname})`;
    if (nickname) return nickname;
    if (name) return name;
    return "요청자";
};

export default function InstructorLessonRequests() {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const [activeStatus, setActiveStatus] = useState(LESSON_REQUEST_STATUS.PENDING);
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    const loadRequests = useCallback(async () => {
        if (!isInstructorUser(userInfo)) return;
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const data = await fetchInstructorLessonRequests(activeStatus);
            setRequests(normalizeLessonRequestList(data));
        } catch (error) {
            setErrorMessage(getLessonRequestErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    }, [activeStatus, userInfo]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    if (!userInfo) {
        return <Navigate to="/login" replace />;
    }

    if (!isInstructorUser(userInfo)) {
        return <Navigate to="/lesson-requests" replace />;
    }

    return (
        <div className="lesson-request-page lesson-request-page--list">
            <SubHeader title="받은 수업 요청" />
            <nav className="lesson-request-tabs" aria-label="받은 수업 요청 상태 필터">
                {STATUS_TABS.map((status) => (
                    <button
                        type="button"
                        key={status}
                        className={activeStatus === status ? "is-active" : ""}
                        onClick={() => setActiveStatus(status)}
                    >
                        {LESSON_REQUEST_STATUS_LABEL[status]}
                    </button>
                ))}
            </nav>
            <main className="lesson-request-list">
                {isLoading && <LoadingSpinner />}
                {!isLoading && errorMessage && (
                    <div className="lesson-request-state">
                        <NoData title="요청을 불러오지 못했어요" desc={errorMessage} />
                        <button type="button" className="lesson-request-primary" onClick={loadRequests}>다시 불러오기</button>
                    </div>
                )}
                {!isLoading && !errorMessage && requests.length === 0 && (
                    <div className="lesson-request-state">
                        <NoData title="받은 수업 요청이 없어요" desc="학생이 보낸 수업 개설 요청이 여기에 표시돼요." />
                    </div>
                )}
                {!isLoading && !errorMessage && requests.map((request) => {
                    const meta = getRequestTypeMeta(request.request_type);
                    const scheduleLabel = [
                        request.requested_date ? dayjs(request.requested_date).format("YYYY.MM.DD") : "날짜 미정",
                        `${formatTime(request.requested_start_time)}-${formatTime(request.requested_end_time)}`,
                    ].filter(Boolean).join(" · ");
                    return (
                        <button
                            type="button"
                            key={request.id}
                            className="lesson-request-list-card lesson-request-list-card--clickable"
                            onClick={() => navigate(`/lesson-requests/${request.id}`)}
                        >
                            <div className="lesson-request-list-card__top">
                                <div>
                                    <strong>{getCardTitle(request, meta)}</strong>
                                    <small>{getCardSubtitle(request, meta)}</small>
                                </div>
                                <Badge 
                                  text={ LESSON_REQUEST_STATUS_LABEL[request?.status] || request?.status_display || request?.status || "상태 미정"} 
                                  type="soft" 
                                  color={LESSON_REQUEST_STATUS_TONE[request?.status]}/>
                            </div>
                            <dl>
                                <div>
                                    <dt>수강생</dt>
                                    <dd>{getRequesterLabel(request?.requester)}</dd>
                                </div>
                                <div>
                                    <dt>일정</dt>
                                    <dd>{scheduleLabel}</dd>
                                </div>
                                <div>
                                    <dt>장소</dt>
                                    <dd>{request.location_name || "장소 미정"}</dd>
                                </div>
                                {request.topic && request.request_type !== "SPECIAL" && (
                                    <div>
                                        <dt>{getTopicLabel(request)}</dt>
                                        <dd>{request.topic}</dd>
                                    </div>
                                )}
                            </dl>
                        </button>
                    );
                })}
            </main>
        </div>
    );
}
